import React from 'react';
import { Path, UseFormSetError } from 'react-hook-form';

import { generateObjectPaths } from '@britishcouncil/common-core';
import { usePrevious } from '@britishcouncil/react-common';

import { FieldError, GeneralError, ServerErrorResult } from 'const';
import { ServerError } from 'core';

type Action<T> =
  | {
      type: 'Clear errors';
    }
  | {
      type: 'Set errors';
      generalErrors: GeneralError[];
      fieldErrors: FieldError<T>[];
    };

const serverErrorsReducer = <T>(
  state: ServerErrorResult<T>,
  action: Action<T>
): ServerErrorResult<T> => {
  switch (action.type) {
    case 'Clear errors':
      return { ...state, generalErrors: [], fieldErrors: [] };
    case 'Set errors':
      return { ...state, generalErrors: action.generalErrors, fieldErrors: action.fieldErrors };
  }
};

export const useServerErrors = <T extends Record<string, unknown>>(
  values: T,
  serverValidationErrors?: ServerError[],
  formErrorSetter?: UseFormSetError<T>
): ServerErrorResult<T> & { clearServerErrors: () => void } => {
  const previousErrors = usePrevious(serverValidationErrors);
  const fields = React.useMemo(() => new Set(generateObjectPaths(values)), [values]);
  const [state, dispatch] = React.useReducer<ServerErrorResult<T>, [Action<T>]>(
    serverErrorsReducer,
    {
      generalErrors: [],
      fieldErrors: [],
    }
  );

  const clearServerErrors = React.useCallback(() => dispatch({ type: 'Clear errors' }), []);

  const transformFieldnameToRHFModel = React.useCallback(
    (fieldname: string) => fieldname.replaceAll('[', '.').replaceAll(']', '') as Path<T>,
    []
  );

  React.useEffect(() => {
    if (previousErrors === serverValidationErrors) return;
    if (serverValidationErrors && Array.isArray(serverValidationErrors)) {
      const fieldErrors: FieldError<T>[] = [];
      const generalErrors: GeneralError[] = [];

      for (const { message, propertyName } of serverValidationErrors) {
        if (propertyName && fields.has(propertyName)) {
          fieldErrors.push({
            propertyName: transformFieldnameToRHFModel(propertyName),
            message,
          });
        } else {
          generalErrors.push({
            message,
          });
        }
      }

      dispatch({ type: 'Set errors', generalErrors, fieldErrors });
    }
  }, [fields, serverValidationErrors, transformFieldnameToRHFModel, previousErrors]);

  React.useEffect(() => {
    state.fieldErrors.forEach((fieldError, index) => {
      formErrorSetter?.(fieldError.propertyName, {
        type: `custom-server-error-${index}`,
        message: fieldError.message,
      });
    });
  }, [state.fieldErrors, formErrorSetter]);

  return { ...state, clearServerErrors };
};
